import { useEffect, useState } from "react";
import { useTranslation } from "../i18n";
import { motion, AnimatePresence } from "framer-motion";
import { RiCloseLine, RiRestartLine, RiDeleteBinLine, RiImage2Fill } from "react-icons/ri";
import type { Job } from "../types/job";
import { jobErrorText } from "../lib/jobError";
import { formatModel, hasScene, jobDisplayName, statusText } from "../lib/jobDisplay";
import AudioPlayer from "./AudioPlayer";
import TextToImageModal from "./TextToImageModal";
import PodcastPreviewModal from "./PodcastPreviewModal";
import PodcastAudioPreviewModal from "./PodcastAudioPreviewModal";
import AvatarGenerationModal from "./AvatarGenerationModal";
import VideoGenerationModal from "./VideoGenerationModal";
import ImageGenerationMultiSelectModal from "./ImageGenerationMultiSelectModal";
import { isQuestionBatchType, getQuestionBatchInfo } from "../lib/questionBatch";
import { withMwRoot } from "../lib/mwRoot";
import { parseAreaOpsPrompt } from "../lib/areaOpsPrompt";
import AreaOpsPromptList from "./AreaOpsPromptList";

interface JobModalProps {
  isOpen: boolean;
  onClose: () => void;
  job: Job;
  onApprove: (jobId: Job) => Promise<void>;
  onDecline: (jobId: string) => Promise<void>;
  onDismiss?: (jobId: string) => Promise<void>;
  showHistory?: boolean;
}

export default function JobModal({
  isOpen,
  onClose,
  job,
  onApprove,
  onDecline,
  onDismiss,
  showHistory = false,
}: JobModalProps) {
  const { t, i18n } = useTranslation();
  const [isApproving, setIsApproving] = useState(false);
  const [isDeclining, setIsDeclining] = useState(false);
  const [imageKey, setImageKey] = useState(0);
  // Full-page lightbox for the Sonra (result) image.
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  // Broken/404 image fallback — shows a quiet placeholder instead of the browser's
  // broken-image icon. Reset per job so a previous job's failure doesn't stick.
  const [sourceImgError, setSourceImgError] = useState(false);
  const [resultImgError, setResultImgError] = useState(false);
  useEffect(() => {
    setSourceImgError(false);
    setResultImgError(false);
  }, [job.id]);

  // Seslendirme (text-to-speech): onayla/reddet yok, sadece dinle
  const isTts =
    (job.type || "")
      .toLowerCase()
      .trim()
      .replace(/[\s_]+/g, "-") === "text-to-speech";
  // Sahne anlatımı (training-narration): bitmiş iş, onayla/reddet yok
  const isNarration =
    (job.type || "")
      .toLowerCase()
      .trim()
      .replace(/[\s_]+/g, "-") === "training-narration";
  // Soru oluşturma (question-batch-generate): sahne gizli, ilerleme + üretilen sorular
  const isQuestionBatch = isQuestionBatchType(job);
  const qb = isQuestionBatch ? getQuestionBatchInfo(job) : null;
  // Toplu Düzenleme (area-ops-batch): prompt is a numbered op summary → list.
  const areaOps = parseAreaOpsPrompt(job.prompt);

  // Ses kaynağını topla: result.audioUrl / metadata.audioUrl veya mp3 olan resultUrl
  const isAudioFile = (u?: string | null) => !!u && /\.(mp3|wav|m4a|aac|oga)(\?|#|$)/i.test(u);
  const audioUrl =
    (isAudioFile(job.resultUrl) ? job.resultUrl! : "") ||
    (isTts
      ? (job.result?.audioUrl as string | undefined) ||
        (job.data?.audioUrl as string | undefined) ||
        (job.metadata?.audioUrl as string | undefined)
      : undefined) ||
    "";
  // resultUrl ses dosyasıysa görsel önizlemede gösterme (aşağıda ayrı oynatıcı var)
  // Text Extract (extract-text): sadece kaynak (önceki) görseli göster, sonucu gizle
  const isTextExtract =
    ((job as any).operation || job.metadata?.operation || "").toString().toLowerCase() ===
      "extract-text" || (job.name || "").toLowerCase().replace(/[\s-]+/g, "_") === "extract_text";
  const visualResultUrl = isTextExtract ? "" : isAudioFile(job.resultUrl) ? "" : job.resultUrl;
  // The "before" media; a user upload can be a host-less /api/… path.
  const sourceUrl = withMwRoot(job.sourceUrl);

  const handleApprove = async () => {
    try {
      setIsApproving(true);
      await onApprove(job);
      onClose();
    } finally {
      setIsApproving(false);
    }
  };

  const handleDecline = async () => {
    try {
      setIsDeclining(true);
      await onDecline(job.id);
      onClose();
    } finally {
      setIsDeclining(false);
    }
  };

  const handleReplayImage = () => {
    setImageKey((prev) => prev + 1);
  };

  // Reset the lightbox when the modal itself closes, so reopening
  // (possibly for a different job) starts back at the detail view.
  useEffect(() => {
    if (!isOpen) setIsPreviewOpen(false);
  }, [isOpen]);

  // Esc returns to the detail view while previewing; from the detail view it
  // closes the modal (same callback as the X). Only attached while mounted.
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (isPreviewOpen) setIsPreviewOpen(false);
      else onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, isPreviewOpen, onClose]);

  const handleDismiss = async () => {
    if (onDismiss) {
      await onDismiss(job.id);
      onClose();
    }
  };

  // İnfografik sahne (infographic-scene-generate): birden çok üretilen görsel gösterilir
  // ama TEK onay ile — her görselde ayrı onayla yok.
  const normInfographicType = (job.type || "")
    .toLowerCase()
    .trim()
    .replace(/[\s_]+/g, "-");
  const isInfographicScene =
    normInfographicType === "infographic-scene-generate" ||
    normInfographicType === "infographic-images-generate" ||
    normInfographicType === "infographic-image-generate";
  const infographicImages: string[] = isInfographicScene
    ? job.metadata?.generatedImages?.map((g: any) => g?.url).filter(Boolean) ||
      job.metadata?.images ||
      (job.resultUrl ? [job.resultUrl] : [])
    : [];

  // Use ImageGenerationMultiSelectModal for jobs with name "Generate Similar" and multiple images
  const isMultiImageGeneration =
    job.name === "Generate Similar" && job.metadata?.generatedImages?.length > 0;

  if (isMultiImageGeneration) {
    return (
      <ImageGenerationMultiSelectModal
        isOpen={isOpen}
        onClose={onClose}
        job={job}
        onApprove={onApprove}
        onDecline={onDecline}
        onDismiss={onDismiss}
        showHistory={showHistory}
      />
    );
  }

  // Use TextToImageModal for text-to-image type (sol prompt / sağ üretilen görsel)
  if (
    (job.type || "")
      .toLowerCase()
      .trim()
      .replace(/[\s_]+/g, "-") === "text-to-image"
  ) {
    return (
      <TextToImageModal
        isOpen={isOpen}
        onClose={onClose}
        job={job}
        onApprove={onApprove}
        onDecline={onDecline}
        onDismiss={onDismiss}
        showHistory={showHistory}
      />
    );
  }

  // Use PodcastPreviewModal for podcast-text type
  if (job.type === "podcast-text") {
    return (
      <PodcastPreviewModal
        isOpen={isOpen}
        onClose={onClose}
        job={job}
        onApprove={onApprove}
        onDecline={onDecline}
        onDismiss={onDismiss}
        showHistory={showHistory}
      />
    );
  }

  // Use PodcastAudioPreviewModal for podcast-audio type
  if (job.type === "podcast-audio") {
    return (
      <PodcastAudioPreviewModal
        isOpen={isOpen}
        onClose={onClose}
        job={job}
        onApprove={onApprove}
        onDecline={onDecline}
        onDismiss={onDismiss}
        showHistory={showHistory}
      />
    );
  }

  // Use VideoGenerationModal for video generation type (generic + explicit)
  const isVideoJob =
    job.type.toLowerCase().includes("video") ||
    [
      "generate-staged-all",
      "init-staged",
      "generate-staged-segment",
      "staged_video_init",
      "staged_video_generate_all",
      "staged_video_generation",
    ].includes(job.type);
  if (isVideoJob) {
    return (
      <VideoGenerationModal
        isOpen={isOpen}
        onClose={onClose}
        job={job}
        onApprove={onApprove}
        onDecline={onDecline}
        onDismiss={onDismiss}
        showHistory={showHistory}
      />
    );
  }

  // Use AvatarGenerationModal for avatar generation type
  if (
    job.type === "ai-avatar" ||
    job.type === "ai_avatar" ||
    job.type === "tavus-video" ||
    job.type === "tts-avatar" ||
    job.type === "lipsync" ||
    job.type === "image-to-avatar" ||
    job.type === "image_to_avatar_generation" ||
    job.type === "talking-avatar" ||
    job.type === "talking_avatar"
  ) {
    return (
      <AvatarGenerationModal
        isOpen={isOpen}
        onClose={onClose}
        job={job}
        onApprove={onApprove}
        onDecline={onDecline}
        onDismiss={onDismiss}
        showHistory={showHistory}
      />
    );
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-60"
            onClick={onClose}
          />

          <motion.div
            initial={{ x: 40, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 40, opacity: 0 }}
            transition={{ type: "spring", stiffness: 420, damping: 32 }}
            className="fixed top-16 right-4 bottom-4 w-[420px] max-w-[90vw] rounded-lg border border-white/20 bg-white shadow-2xl z-70 overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 via-transparent to-fuchsia-500/5 pointer-events-none" />

            <div className="relative h-full flex flex-col">
              <>
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-black/5">
                  <div className="space-y-0.5">
                    <p className="text-xs tracking-[0.08em] text-gray-500">
                      {t("modal.jobPreviewUpper")}
                    </p>
                    <h2 className="text-lg font-semibold text-gray-800">
                      {jobDisplayName(job, t, i18n)}
                    </h2>
                    {hasScene(job) &&
                      job.type !== "training-aspect-conversion" &&
                      job.type !== "training_aspect_conversion" &&
                      job.type !== "subtitle-generation" &&
                      job.type !== "subtitle_generation" &&
                      job.type !== "training-video-export" &&
                      job.type !== "training_video_export" &&
                      !isNarration &&
                      !isQuestionBatch && (
                        <p className="text-xs text-gray-500">
                          {t("modal.scene")} {job.sceneIndex}
                        </p>
                      )}
                  </div>
                  <div className="flex items-center gap-1">
                    {onDismiss && !showHistory && (
                      <button
                        onClick={handleDismiss}
                        className="p-1.5 hover:bg-red-50 rounded-lg transition-colors"
                        title={t("actions.dismiss")}
                      >
                        <RiDeleteBinLine className="w-4 h-4 text-gray-600 hover:text-red-600" />
                      </button>
                    )}
                    <button
                      onClick={onClose}
                      className="w-11 h-11 flex items-center justify-center hover:bg-black/5 rounded-lg transition-colors"
                    >
                      <RiCloseLine className="w-4 h-4 text-gray-600" />
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {/* Prompt — top */}
                  {job.type === "training-aspect-conversion" ||
                  job.type === "training_aspect_conversion" ? (
                    (() => {
                      const ratio = job.prompt?.match(/(\d+:\d+)/)?.[1];
                      const ratioLabel =
                        ratio && i18n.exists(`trainingAspectConversion.ratios.${ratio}`)
                          ? t(`trainingAspectConversion.ratios.${ratio}`)
                          : ratio;
                      return (
                        <div className="border border-gray-200 rounded-lg p-3 bg-white text-sm">
                          <p className="text-gray-500 text-[11px] tracking-[0.08em] mb-1 uppercase">
                            {t("trainingAspectConversion.target")}
                          </p>
                          <div className="p-2 rounded-md bg-gray-50 border border-gray-100 text-xs text-gray-700 leading-snug">
                            {ratio
                              ? t("trainingAspectConversion.summary", { ratio: ratioLabel })
                              : t("trainingAspectConversion.summaryUnknown")}
                          </div>
                        </div>
                      );
                    })()
                  ) : job.type === "subtitle-generation" || job.type === "subtitle_generation" ? (
                    (() => {
                      const code = job.prompt?.match(/\b([a-z]{2,3})\b\s*$/i)?.[1]?.toLowerCase();
                      const langKey = code ? `subtitleGeneration.languages.${code}` : null;
                      const language =
                        langKey && i18n.exists(langKey) ? t(langKey) : code?.toUpperCase();
                      return (
                        <div className="border border-gray-200 rounded-lg p-3 bg-white text-sm">
                          <p className="text-gray-500 text-[11px] tracking-[0.08em] mb-1 uppercase">
                            {t("subtitleGeneration.target")}
                          </p>
                          <div className="p-2 rounded-md bg-gray-50 border border-gray-100 text-xs text-gray-700 leading-snug">
                            {language
                              ? t("subtitleGeneration.summary", { language })
                              : t("subtitleGeneration.summaryUnknown")}
                          </div>
                        </div>
                      );
                    })()
                  ) : job.type === "training-logo-overlay" ||
                    job.type === "training_logo_overlay" ? (
                    // prompt is a fixed English "Apply logo to all training images"
                    <div className="border border-gray-200 rounded-lg p-3 bg-white text-sm">
                      <p className="text-gray-500 text-[11px] tracking-[0.08em] mb-1 uppercase">
                        {t("trainingLogoOverlay.target")}
                      </p>
                      <div className="p-2 rounded-md bg-gray-50 border border-gray-100 text-xs text-gray-700 leading-snug">
                        {t("trainingLogoOverlay.summary")}
                      </div>
                    </div>
                  ) : isNarration ? (
                    <div className="border border-gray-200 rounded-lg p-3 bg-white text-sm">
                      <div className="p-2 rounded-md bg-gray-50 border border-gray-100 text-xs text-gray-700 leading-snug">
                        {t("jobCard.narrationDone", {
                          count: Number(job.prompt?.match(/\d+/)?.[0] ?? 0),
                        })}
                      </div>
                    </div>
                  ) : (
                    job.type !== "training-video-export" &&
                    job.type !== "training_video_export" &&
                    !isQuestionBatch &&
                    job.prompt &&
                    (areaOps ? (
                      <AreaOpsPromptList ops={areaOps} />
                    ) : (
                      <div className="border border-gray-200 rounded-lg p-3 bg-white text-sm">
                        <p className="text-gray-500 text-[11px] tracking-[0.08em] mb-1">
                          {t("modal.promptUpper")}
                        </p>
                        <div className="p-2 rounded-md bg-gray-50 border border-gray-100 max-h-28 overflow-y-auto text-xs text-gray-700 leading-snug whitespace-pre-wrap">
                          {job.prompt}
                        </div>
                      </div>
                    ))
                  )}

                  {/* Soru oluşturma: üretim sırasında ilerleme, bitince üretilen sorular alt alta */}
                  {isQuestionBatch &&
                    qb &&
                    (qb.done ? (
                      <div>
                        <h3 className="text-[11px] font-semibold text-gray-600 uppercase tracking-[0.08em] mb-2">
                          {t("questionBatch.questionsUpper")}
                        </h3>
                        {qb.questions.length === 0 ? (
                          <p className="text-xs text-gray-500">
                            {t("questionBatch.generatedCount", { count: qb.total })}
                          </p>
                        ) : (
                          <div className="space-y-3">
                            {qb.questions.map((q, i) => {
                              const isTF = q.kind === "TrueFalse";
                              const isSeq = q.kind === "Ordering" || q.kind === "DragDrop";
                              return (
                                <div
                                  key={i}
                                  className="border border-gray-200 rounded-lg p-3 bg-white space-y-2"
                                >
                                  <div className="flex items-center justify-between gap-2">
                                    <span className="text-[11px] font-semibold text-purple-600">
                                      {t("questionBatch.questionNumber", { number: i + 1 })}
                                    </span>
                                    <span className="text-[10px] font-medium text-gray-500 bg-gray-100 rounded-full px-2 py-0.5">
                                      {t(`questionBatch.types.${q.kind}`)}
                                    </span>
                                  </div>
                                  <p className="text-sm text-gray-800 leading-snug">{q.question}</p>
                                  {q.pairs && q.pairs.length > 0 ? (
                                    <ul className="space-y-1">
                                      {q.pairs.map((p, j) => (
                                        <li
                                          key={j}
                                          className="flex items-center gap-2 text-xs leading-snug px-2 py-1.5 rounded-md bg-emerald-50 text-emerald-700"
                                        >
                                          <span className="break-words flex-1 font-medium">
                                            {p.left}
                                          </span>
                                          <span className="shrink-0 text-emerald-500">→</span>
                                          <span className="break-words flex-1">{p.right}</span>
                                        </li>
                                      ))}
                                    </ul>
                                  ) : q.blanks && q.blanks.length > 0 ? (
                                    <div className="text-xs px-2 py-1.5 rounded-md bg-emerald-50 text-emerald-700 font-medium">
                                      {t("questionBatch.answerLabel")}: {q.blanks.join(", ")}
                                    </div>
                                  ) : q.answers.length > 0 ? (
                                    <ul className="space-y-1">
                                      {q.answers.map((a, j) => (
                                        <li
                                          key={j}
                                          className={`flex items-start gap-2 text-xs leading-snug px-2 py-1.5 rounded-md ${
                                            !isTF && !isSeq && a.correct
                                              ? "bg-emerald-50 text-emerald-700 font-medium"
                                              : "bg-gray-50 text-gray-700"
                                          }`}
                                        >
                                          <span className="shrink-0 tabular-nums text-gray-400">
                                            {isSeq && a.order
                                              ? `${a.order}.`
                                              : `${String.fromCharCode(65 + j)}.`}
                                          </span>
                                          <span className="break-words flex-1">{a.text}</span>
                                          {isTF ? (
                                            <span
                                              className={`shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                                                a.correct
                                                  ? "bg-emerald-100 text-emerald-700"
                                                  : "bg-red-100 text-red-600"
                                              }`}
                                            >
                                              {a.correct
                                                ? t("questionBatch.true")
                                                : t("questionBatch.false")}
                                            </span>
                                          ) : !isSeq && a.correct ? (
                                            <span className="shrink-0 text-emerald-600">✓</span>
                                          ) : null}
                                        </li>
                                      ))}
                                    </ul>
                                  ) : (
                                    <p className="text-[11px] text-gray-400 italic">
                                      {t("questionBatch.freeResponseNote")}
                                    </p>
                                  )}
                                  {q.audioUrl && <AudioPlayer key={q.audioUrl} src={q.audioUrl} />}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-2">
                        <p className="text-sm text-gray-700">
                          {qb.total
                            ? t("questionBatch.generating", {
                                total: qb.total,
                                progress: qb.progress,
                              })
                            : t("questionBatch.generatingNoTotal", { progress: qb.progress })}
                        </p>
                        <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-purple-500 to-fuchsia-500 transition-all"
                            style={{ width: `${Math.min(100, Math.max(0, qb.progress))}%` }}
                          />
                        </div>
                      </div>
                    ))}

                  {/* Sahne anlatımları (training-narration): her sahnenin metni + sesi */}
                  {isNarration &&
                    Array.isArray(job.metadata?.sceneNarrations) &&
                    job.metadata.sceneNarrations.length > 0 && (
                      <div className="space-y-3">
                        <h3 className="text-[11px] font-semibold text-gray-600 uppercase tracking-[0.08em]">
                          {t("modal.narrationSegmentsUpper")}
                        </h3>
                        {job.metadata.sceneNarrations.map((seg: any, i: number) => (
                          <div
                            key={i}
                            className="border border-gray-200 rounded-lg p-3 bg-white space-y-2"
                          >
                            <p className="text-gray-500 text-[11px] uppercase tracking-[0.08em]">
                              {t("modal.segment")} {i + 1}
                            </p>
                            {seg?.text && (
                              <div className="p-2 rounded-md bg-gray-50 border border-gray-100 max-h-32 overflow-y-auto text-xs text-gray-700 leading-snug whitespace-pre-wrap">
                                {seg.text}
                              </div>
                            )}
                            {seg?.audioUrl && <AudioPlayer key={seg.audioUrl} src={seg.audioUrl} />}
                          </div>
                        ))}
                      </div>
                    )}

                  {/* Audio Preview (text-to-speech vb.) */}
                  {audioUrl && (
                    <div>
                      <h3 className="text-[11px] font-semibold text-gray-600 uppercase tracking-[0.08em] mb-2">
                        {t("modal.audioPreview")}
                      </h3>
                      <AudioPlayer key={audioUrl} src={audioUrl} />
                    </div>
                  )}

                  {/* Visual Preview */}
                  {!isInfographicScene &&
                    (job.sourceUrl || visualResultUrl) &&
                    (() => {
                      // Sadece dosya uzantısına bak - mp4, webm, ogg, mov ise video player
                      const resultIsVideo = visualResultUrl
                        ? /\.(mp4|webm|ogg|mov)(\?|#|$)/i.test(visualResultUrl)
                        : false;
                      // Extend/Convert animasyon işlerinde "önce" kaynağı da bir video
                      // olabilir (önceki animasyon çıktısının uzatılması) — img yerine
                      // video player kullan (bkz. AvatarGenerationModal aynı desen).
                      const sourceIsVideo = job.sourceUrl
                        ? /\.(mp4|webm|ogg|mov)(\?|#|$)/i.test(job.sourceUrl)
                        : false;
                      return (
                        <div>
                          <h3 className="text-[11px] font-semibold text-gray-600 uppercase tracking-[0.08em] mb-2">
                            {t("modal.visualPreview")}
                          </h3>
                          {/* Stacked, not side-by-side: Önce (source) small on top, Sonra
                            (result) larger below — both fit the modal without scrolling. */}
                          <div className="space-y-3">
                            {job.sourceUrl && (
                              <div className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm">
                                {!isTextExtract && (
                                  <div className="px-3 py-2 text-xs font-semibold text-gray-600">
                                    {t("modal.before")}
                                  </div>
                                )}
                                <div className="bg-gray-50 flex items-center justify-center">
                                  {sourceIsVideo ? (
                                    <video
                                      src={sourceUrl}
                                      controls
                                      preload="metadata"
                                      className="max-h-[30vh] w-full object-contain"
                                    />
                                  ) : sourceImgError ? (
                                    <RiImage2Fill className="w-7 h-7 text-gray-300 my-8" />
                                  ) : (
                                    <img
                                      src={sourceUrl}
                                      alt={t("modal.before")}
                                      className="max-h-[30vh] w-full object-contain"
                                      onError={() => setSourceImgError(true)}
                                    />
                                  )}
                                </div>
                              </div>
                            )}
                            {visualResultUrl && (
                              <div className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm">
                                <div className="px-3 py-2 text-xs font-semibold text-gray-600">
                                  {job.sourceUrl ? t("modal.after") : t("modal.result")}
                                </div>
                                <div
                                  className="bg-gray-50 flex items-center justify-center"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {resultIsVideo ? (
                                    <video
                                      key={visualResultUrl}
                                      src={visualResultUrl}
                                      controls
                                      preload="metadata"
                                      className="max-h-[50vh] w-full object-contain"
                                    />
                                  ) : resultImgError ? (
                                    <RiImage2Fill className="w-7 h-7 text-gray-300 my-8" />
                                  ) : (
                                    <div className="relative group w-full">
                                      <img
                                        src={`${visualResultUrl}${visualResultUrl?.includes("?") ? "&" : "?"}t=${imageKey}`}
                                        alt={t("modal.after")}
                                        className="max-h-[50vh] w-full object-contain"
                                        onError={() => setResultImgError(true)}
                                      />
                                      {/* Replay button for gif/webp */}
                                      {/\.(gif|webp)(\?|#|$)/i.test(visualResultUrl || "") && (
                                        <button
                                          onClick={handleReplayImage}
                                          className="absolute top-2 right-2 p-1.5 bg-white/90 hover:bg-white rounded-lg shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                                          title={t("modal.replay") || "Replay"}
                                        >
                                          <RiRestartLine className="w-4 h-4 text-gray-700" />
                                        </button>
                                      )}
                                    </div>
                                  )}
                                </div>
                                {!resultIsVideo && (
                                  <div className="p-2">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setIsPreviewOpen(true);
                                      }}
                                      className="w-full py-2 rounded-lg border border-gray-200 text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                                    >
                                      {t("actions.preview")}
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })()}

                  {/* Text Extract: çıkarılan metinlerin listesi */}
                  {isTextExtract &&
                    (() => {
                      const meta = (job.metadata ?? {}) as any;
                      const texts: string[] = Array.isArray(meta.extractedTexts)
                        ? meta.extractedTexts.filter(Boolean)
                        : Array.isArray(meta.details?.textBoxes)
                          ? meta.details.textBoxes.map((b: any) => b?.content).filter(Boolean)
                          : [];
                      if (texts.length === 0) return null;
                      return (
                        <div>
                          <h3 className="text-[11px] font-semibold text-gray-600 uppercase tracking-[0.08em] mb-2">
                            {t("modal.extractedTextsUpper")}
                          </h3>
                          <div className="border border-gray-200 rounded-lg bg-white divide-y divide-gray-100 max-h-60 overflow-y-auto">
                            {texts.map((txt, i) => (
                              <div
                                key={i}
                                className="flex items-start gap-2 px-3 py-2 text-xs text-gray-700"
                              >
                                <span className="text-gray-400 tabular-nums shrink-0">
                                  {i + 1}.
                                </span>
                                <span className="leading-snug break-words">{txt}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })()}

                  {/* İnfografik sahne: üretilen görseller (tek onay, her görselde ayrı onay yok) */}
                  {isInfographicScene && infographicImages.length > 0 && (
                    <div>
                      <h3 className="text-[11px] font-semibold text-gray-600 uppercase tracking-[0.08em] mb-2">
                        {t("modal.generatedImagesUpper")}
                      </h3>
                      <div className="grid grid-cols-2 gap-3">
                        {infographicImages.map((url, i) => (
                          <div
                            key={i}
                            className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm"
                          >
                            <img
                              src={url}
                              alt={`${i + 1}`}
                              className="w-full h-auto object-contain bg-gray-50"
                              style={{ maxHeight: "220px" }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {job.error && (
                    <div className="border border-red-200 rounded-lg p-3 bg-red-50 text-sm">
                      <p className="text-red-700 text-[11px] uppercase tracking-[0.08em] mb-1">
                        {t("jobCard.error")}
                      </p>
                      <p className="text-red-700 text-xs leading-snug">{jobErrorText(job, t)}</p>
                    </div>
                  )}

                  {/* Parameters */}
                  {(() => {
                    const hideModel =
                      job.type === "training-aspect-conversion" ||
                      job.type === "training_aspect_conversion" ||
                      job.type === "subtitle-generation" ||
                      job.type === "subtitle_generation";
                    return (
                      <div
                        className={`grid ${hideModel ? "grid-cols-2" : "grid-cols-3"} gap-3 text-sm`}
                      >
                        {!hideModel && (
                          <div>
                            <p className="text-gray-500 text-[11px] uppercase tracking-[0.08em]">
                              {t("jobCard.model")}
                            </p>
                            <p className="text-gray-800 font-semibold break-words">
                              {formatModel(job.model, t) || t("status.unknown")}
                            </p>
                          </div>
                        )}
                        <div>
                          <p className="text-gray-500 text-[11px] uppercase tracking-[0.08em]">
                            {t("jobCard.created")}
                          </p>
                          <p className="text-gray-800 font-semibold">
                            {new Date(job.createdAt).toLocaleDateString(
                              i18n.language === "tr" ? "tr-TR" : "en-US"
                            )}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500 text-[11px] uppercase tracking-[0.08em]">
                            {t("jobCard.status")}
                          </p>
                          <p className="text-gray-800 font-semibold capitalize">
                            {statusText(job.status, t, i18n)}
                          </p>
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {/* Footer */}
                {!showHistory &&
                  !isTts &&
                  !isNarration &&
                  (() => {
                    const hideDecline =
                      job.type === "training-aspect-conversion" ||
                      job.type === "training_aspect_conversion" ||
                      job.type === "subtitle-generation" ||
                      job.type === "subtitle_generation" ||
                      job.type === "training-video-export" ||
                      job.type === "training_video_export";
                    return (
                      <div className="border-t border-gray-200 px-4 py-3 flex gap-2 bg-white">
                        {!hideDecline && (
                          <button
                            onClick={handleDecline}
                            disabled={isDeclining || isApproving}
                            className="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg bg-white hover:bg-red-50 text-red-600 text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-red-200"
                          >
                            {isDeclining ? t("actions.declining") : t("actions.decline")}
                          </button>
                        )}

                        {job?.status === "inspect" && (
                          <button
                            onClick={handleApprove}
                            disabled={isApproving || isDeclining}
                            className="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                          >
                            {isApproving ? t("actions.approving") : t("actions.approve")}
                          </button>
                        )}
                      </div>
                    );
                  })()}
              </>
            </div>
          </motion.div>
        </>
      )}
      {isOpen && isPreviewOpen && visualResultUrl && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="fixed inset-0 bg-black/60 z-[80] flex items-center justify-center"
          onClick={() => setIsPreviewOpen(false)}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsPreviewOpen(false);
            }}
            className="absolute top-4 right-4 w-11 h-11 flex items-center justify-center rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors"
            aria-label={t("actions.close")}
          >
            <RiCloseLine className="w-6 h-6" />
          </button>
          {resultImgError ? (
            <RiImage2Fill className="w-10 h-10 text-white/40" />
          ) : (
            <img
              src={visualResultUrl}
              alt={t("modal.after")}
              className="max-w-[90vw] max-h-[90vh] object-contain"
              onClick={(e) => e.stopPropagation()}
              onError={() => setResultImgError(true)}
            />
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
